/**
 * E-043 Texas Flood Compliance API client (F-242 / F-243 / F-244 / F-245).
 *
 * Consumes the canonical flood route group mounted at `/api/texas/flood/*`
 * and `/texas/flood/*` in `landlord-pro/src/routes/flood.js`:
 *   GET  /meta                       (handleMeta,     route flood.js:277)
 *   GET  /lookup?lat=&lon= | ?address (handleLookup,   flood.js:278)
 *   POST /zone                       (handleSaveZone, flood.js:279)
 *   GET  /property/:id               (handleGetZone, flood.js:280)
 *   GET  /property/:id/years         (handleListYears, flood.js:281)
 *   GET  /history?property_id=       (handleHistory, flood.js:282)
 *   POST /history/ingest             (handleIngestHistory, flood.js:283)
 *   GET  /disclosure                 (handleGenerateNotice, flood.js:284)
 *   POST /disclosure/issue           (handleIssueNotice, flood.js:285)
 *   POST /disclosure/acknowledge     (handleAcknowledge, flood.js:286)
 *
 * All calls use `landlordApi` (Authorization + X-Jurisdiction injected by
 * api.ts). Tenant scoping is enforced server-side from the JWT — the client
 * never sends a tenantId. Every method is fail-soft: transport and 4xx/5xx
 * errors resolve to a safe default instead of throwing, so a degraded NFHL
 * or a missing record never blanks the page.
 */

import { landlordApi } from './api';

export interface FloodZoneLookup {
    zoneCode: string | null;
    zoneRaw: string | null;
    zoneSubtype: string | null;
    zoneClassification: string;
    inFloodplain: boolean;
    status: string;
    source: string;
    panelNumber: string | null;
    communityNumber: string | null;
    effectiveDate: string | null;
    staticBfe: number | null;
    lat: number | null;
    lon: number | null;
    degraded: boolean;
    degradedMode: string | null;
    degradedDetail: string | null;
    retried?: boolean;
    queriedAt?: string;
    address?: string | null;
}

export interface FloodZoneRecord {
    pk: string;
    sk: string;
    propertyId: string;
    zoneCode: string | null;
    zoneRecognised: boolean;
    zoneClassification: string;
    inFloodplain: boolean;
    source: string;
    status: string;
    refreshedAt: string | null;
    refreshedAtMs: number | null;
    createdAt: string | null;
    stale?: boolean;
    refreshDueAt?: string | null;
    degraded?: boolean;
    year?: string;
    partition?: string;
}

export interface FloodHistoryEvent {
    occurredAt: string;
    source: string;
    eventType: string | null;
    severity: string | null;
    summary?: string | null;
}

export interface FloodHistory {
    propertyId: string;
    windowYears: number;
    windowStart: string;
    generatedAt: string;
    eventCount: number;
    events: FloodHistoryEvent[];
    hasHistory: boolean;
}

export interface FloodHistorySourceResult {
    propertyId: string;
    source: string;
    collected: number;
    rejected: number;
    error?: string | null;
}

export interface FloodIngestResult {
    propertyId: string;
    windowStart: string;
    windowYears: number;
    collected: number;
    rejected: number;
    sources: FloodHistorySourceResult[];
}

export interface FloodMeta {
    epic: string;
    features: string[];
    citation: string;
    partition: string;
    nfhl: Record<string, unknown>;
    nfhlService: string;
    zoneCodes: string[];
    recordStatus: string[];
    eventSources: string[];
    vault: string;
    refreshCycleYears: number;
    historyWindowYears: number;
    bilingual: string[];
    failSoft: boolean;
}

export interface FloodDetermination {
    verified: boolean;
    degraded: boolean;
    degradedMode: string | null;
    zoneCode: string | null;
    zoneRaw: string | null;
    zoneSubtype: string | null;
    zoneClassification: string | null;
    inFloodplain: boolean;
    mandatoryFloodInsuranceLikely: boolean;
    panelNumber: string | null;
    communityNumber: string | null;
    effectiveDate: string | null;
    baseFloodElevation: number | null;
    determinationStale: boolean;
    refreshCycleYears: number;
}

export interface FloodNotice {
    noticeId: string;
    propertyId: string;
    tenantId: string;
    generatedAt: string;
    citation: string;
    property: {
        address: string | null;
        city: string | null;
        state: string;
        zip: string | null;
        lat: number | null;
        lon: number | null;
    };
    determination: FloodDetermination;
    history: {
        windowYears: number;
        windowStart: string;
        eventCount: number;
        events: FloodHistoryEvent[];
    };
    panelMap: { url: string; panelNumber: string | null; embedded: boolean };
    text: {
        en: { heading: string; citation: string; zoneStatement: string; historyStatement: string; insuranceStatement: string; buyerAckLine: string };
        es: { heading: string; citation: string; zoneStatement: string; historyStatement: string; insuranceStatement: string; buyerAckLine: string };
    };
    evidenceVault: string;
    s3Reference: string;
    isComplete: boolean;
    record?: Record<string, unknown>;
}

export interface FloodAcknowledgment {
    acknowledgmentId: string;
    noticeId: string;
    propertyId: string;
    signerName: string;
    signerRole: string | null;
    signatureMethod: string;
    signedAt: string;
    evidenceVault: string;
    s3Reference: string;
    determinationAtSigning: {
        zoneCode: string | null;
        panelNumber: string | null;
        verified: boolean;
    };
    createdAt: string;
}

/** Unwrap the BE `{ success, data }` envelope. */
function payloadOf<T>(res: any, fallback: T): T {
    try {
        const body = res && res.data;
        if (body && typeof body === 'object' && 'data' in body) {
            return (body.data ?? fallback) as T;
        }
        return (body ?? fallback) as T;
    } catch {
        return fallback;
    }
}

/** FAIL_OK default data values — matching tenants, never tenant-scoped. */
const EMPTY_META: FloodMeta = {
    epic: 'E-043',
    features: ['F-242', 'F-243', 'F-244', 'F-245'],
    citation: 'Texas Property Code §92.256',
    partition: 'TX_FLOOD#{propertyId}#YYYY',
    nfhl: {},
    nfhlService: '',
    zoneCodes: [],
    recordStatus: [],
    eventSources: [],
    vault: 'compliance-evidence-vault',
    refreshCycleYears: 5,
    historyWindowYears: 5,
    bilingual: ['en', 'es'],
    failSoft: true,
};

const EMPTY_ZONE: FloodZoneLookup = {
    zoneCode: null,
    zoneRaw: null,
    zoneSubtype: null,
    zoneClassification: '',
    inFloodplain: false,
    status: '',
    source: '',
    panelNumber: null,
    communityNumber: null,
    effectiveDate: null,
    staticBfe: null,
    lat: null,
    lon: null,
    degraded: false,
    degradedMode: null,
    degradedDetail: null,
};

const EMPTY_HISTORY: FloodHistory = {
    propertyId: '',
    windowYears: 5,
    windowStart: '',
    generatedAt: '',
    eventCount: 0,
    events: [],
    hasHistory: false,
};

export const floodApi = {
    /** GET /meta — feature flag surface, zone vocabulary, refresh cadence. */
    getMeta: async (): Promise<FloodMeta> => {
        try {
            const res = await landlordApi.get('api/texas/flood/meta');
            return payloadOf<FloodMeta>(res, EMPTY_META);
        } catch {
            return EMPTY_META;
        }
    },

    /** GET /lookup?lat=&lon= — NFHL zone determination for a coordinate. */
    lookupByLatLon: async (lat: number | string, lon: number | string): Promise<FloodZoneLookup> => {
        try {
            const res = await landlordApi.get('api/texas/flood/lookup', { params: { lat, lon } });
            return payloadOf<FloodZoneLookup>(res, EMPTY_ZONE);
        } catch {
            return EMPTY_ZONE;
        }
    },

    /** GET /lookup?address= — same determination via the caller's geocoder. */
    lookupByAddress: async (address: string): Promise<FloodZoneLookup> => {
        try {
            const res = await landlordApi.get('api/texas/flood/lookup', { params: { address } });
            return payloadOf<FloodZoneLookup>(res, { ...EMPTY_ZONE, address });
        } catch {
            return { ...EMPTY_ZONE, address };
        }
    },

    /** POST /zone — persist a zone determination (manual source or NFHL refresh). */
    saveZone: async (payload: Partial<FloodZoneRecord>): Promise<FloodZoneRecord | null> => {
        try {
            const res = await landlordApi.post('api/texas/flood/zone', payload);
            return payloadOf<FloodZoneRecord | null>(res, null);
        } catch {
            return null;
        }
    },

    /** GET /property/:id — current determination with freshness metadata. 404 → null. */
    getZone: async (propertyId: string): Promise<FloodZoneRecord | null> => {
        try {
            const res = await landlordApi.get(`api/texas/flood/property/${encodeURIComponent(propertyId)}`);
            return payloadOf<FloodZoneRecord | null>(res, null);
        } catch {
            return null;
        }
    },

    /** GET /property/:id/years — every stored determination year, newest first. */
    listYears: async (propertyId: string): Promise<FloodZoneRecord[]> => {
        try {
            const res = await landlordApi.get(`api/texas/flood/property/${encodeURIComponent(propertyId)}/years`);
            return payloadOf<FloodZoneRecord[]>(res, []);
        } catch {
            return [];
        }
    },

    /** GET /history?property_id= — the 5-year flood history. */
    getHistory: async (propertyId: string): Promise<FloodHistory> => {
        try {
            const res = await landlordApi.get('api/texas/flood/history', {
                params: { property_id: propertyId },
            });
            return payloadOf<FloodHistory>(res, EMPTY_HISTORY);
        } catch {
            return EMPTY_HISTORY;
        }
    },

    /** POST /history/ingest — pull recorded events from the supplied feeds. */
    ingestHistory: async (
        propertyId: string,
        feeds: Record<string, unknown[]> = {}
    ): Promise<FloodIngestResult | null> => {
        try {
            const res = await landlordApi.post('api/texas/flood/history/ingest', {
                property_id: propertyId,
                feeds,
            });
            return payloadOf<FloodIngestResult | null>(res, null);
        } catch {
            return null;
        }
    },

    /** GET /disclosure — best-effort, non-persisted preview. May be unavailable. */
    previewNotice: async (propertyId: string): Promise<FloodNotice | null> => {
        try {
            const res = await landlordApi.get('api/texas/flood/disclosure', {
                params: { property_id: propertyId },
            });
            return payloadOf<FloodNotice | null>(res, null);
        } catch {
            return null;
        }
    },

    /** POST /disclosure/issue — generate + persist the notice (201). */
    issueNotice: async (propertyId: string, options: Record<string, unknown> = {}): Promise<FloodNotice | null> => {
        try {
            const res = await landlordApi.post('api/texas/flood/disclosure/issue', {
                property_id: propertyId,
                ...options,
            });
            return payloadOf<FloodNotice | null>(res, null);
        } catch {
            return null;
        }
    },

    /** POST /disclosure/acknowledge — capture the signed acknowledgment (201). */
    acknowledge: async (
        noticeId: string,
        signature: { signerName: string; signerRole?: string; method?: string; signedAt?: string }
    ): Promise<FloodAcknowledgment | null> => {
        try {
            const res = await landlordApi.post('api/texas/flood/disclosure/acknowledge', {
                notice_id: noticeId,
                signerName: signature.signerName,
                signerRole: signature.signerRole || null,
                method: signature.method || 'typed_name',
                signedAt: signature.signedAt || undefined,
            });
            return payloadOf<FloodAcknowledgment | null>(res, null);
        } catch {
            return null;
        }
    },
};

export default floodApi;